import React, { useState } from 'react';
import { Calculator, Copy, Trash2, Info, ChevronDown, ChevronUp } from 'lucide-react';

const AmortizationCalculatorTool = () => {
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [termUnit, setTermUnit] = useState('years');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleView, setScheduleView] = useState('yearly');

  const calculate = () => {
    const P = parseFloat(loanAmount);
    const annualRate = parseFloat(interestRate);
    let termMonths = parseFloat(loanTerm);

    if (isNaN(P) || isNaN(annualRate) || isNaN(termMonths) || P <= 0 || annualRate < 0 || termMonths <= 0) {
      setResult(null);
      return;
    }

    // Convert term to months
    if (termUnit === 'years') {
      termMonths = termMonths * 12;
    }

    const monthlyRate = annualRate / 100 / 12;
    const n = termMonths;

    // Monthly payment formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    let monthlyPayment;
    if (monthlyRate === 0) {
      monthlyPayment = P / n;
    } else {
      monthlyPayment = P * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    }

    const totalPayment = monthlyPayment * n;
    const totalInterest = totalPayment - P;

    // Generate full amortization schedule
    const schedule = [];
    let balance = P;
    let cumulativePrincipal = 0;
    let cumulativeInterest = 0;

    for (let month = 1; month <= n; month++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      balance -= principalPayment;

      cumulativePrincipal += principalPayment;
      cumulativeInterest += interestPayment;

      schedule.push({
        month,
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance),
        cumulativePrincipal,
        cumulativeInterest
      });
    }

    // Generate yearly summary
    const yearlySchedule = [];
    for (let year = 1; year <= Math.ceil(n / 12); year++) {
      const startMonth = (year - 1) * 12;
      const endMonth = Math.min(year * 12, n);
      const yearMonths = schedule.slice(startMonth, endMonth);
      
      const yearPrincipal = yearMonths.reduce((sum, m) => sum + m.principal, 0);
      const yearInterest = yearMonths.reduce((sum, m) => sum + m.interest, 0);
      const yearTotal = yearMonths.reduce((sum, m) => sum + m.payment, 0);
      
      yearlySchedule.push({
        year,
        principal: yearPrincipal,
        interest: yearInterest,
        total: yearTotal,
        endingBalance: yearMonths[yearMonths.length - 1]?.balance || 0,
        cumulativePrincipal: yearMonths[yearMonths.length - 1]?.cumulativePrincipal || 0,
        cumulativeInterest: yearMonths[yearMonths.length - 1]?.cumulativeInterest || 0
      });
    }

    // First year breakdown for display
    const firstYearPrincipal = yearlySchedule[0]?.principal || 0;
    const firstYearInterest = yearlySchedule[0]?.interest || 0;

    setResult({
      loanAmount: P,
      interestRate: annualRate,
      termMonths: n,
      termYears: n / 12,
      monthlyPayment,
      totalPayment,
      totalInterest,
      principalPercent: (P / totalPayment) * 100,
      interestPercent: (totalInterest / totalPayment) * 100,
      firstYearPrincipal,
      firstYearInterest,
      schedule,
      yearlySchedule
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setLoanAmount('');
    setInterestRate('');
    setLoanTerm('');
    setTermUnit('years');
    setResult(null);
    setShowSchedule(false);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Loan Amount ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
              placeholder="e.g., 250000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="loan-amount-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Interest Rate (% per year)
            </label>
            <input
              type="number"
              step="0.01"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              placeholder="e.g., 6.5"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="interest-rate-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Loan Term
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                step="1"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
                placeholder="e.g., 30"
                className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                data-testid="loan-term-input"
              />
              <select
                value={termUnit}
                onChange={(e) => setTermUnit(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                data-testid="term-unit-select"
              >
                <option value="years">Years</option>
                <option value="months">Months</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-amortization-btn"
      >
        <Calculator size={18} />
        Calculate Amortization
      </button>

      {/* Results Section */}
      {result && (
        <div className="space-y-4" data-testid="amortization-results">
          {/* Monthly Payment */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Monthly Payment</span>
              <button
                onClick={() => copyResult(result.monthlyPayment, 'monthly')}
                className={`p-1.5 rounded transition-colors ${
                  copied === 'monthly' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-monthly"
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="text-3xl font-bold text-green-400" data-testid="monthly-payment-result">
              {formatCurrency(result.monthlyPayment)}
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-300 mb-4">Payment Breakdown</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-3 bg-gray-900 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-400">Total Principal</span>
                  <button
                    onClick={() => copyResult(result.loanAmount, 'principal')}
                    className={`p-1 rounded transition-colors ${
                      copied === 'principal' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                  >
                    <Copy size={12} />
                  </button>
                </div>
                <div className="text-xl font-bold text-blue-400" data-testid="total-principal-result">
                  {formatCurrency(result.loanAmount)}
                </div>
                <div className="text-xs text-gray-500">{result.principalPercent.toFixed(1)}% of total</div>
              </div>
              <div className="p-3 bg-gray-900 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-400">Total Interest</span>
                  <button
                    onClick={() => copyResult(result.totalInterest, 'interest')}
                    className={`p-1 rounded transition-colors ${
                      copied === 'interest' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                  >
                    <Copy size={12} />
                  </button>
                </div>
                <div className="text-xl font-bold text-yellow-400" data-testid="total-interest-result">
                  {formatCurrency(result.totalInterest)}
                </div>
                <div className="text-xs text-gray-500">{result.interestPercent.toFixed(1)}% of total</div>
              </div>
            </div>

            {/* Visual breakdown bar */}
            <div className="mt-4">
              <div className="flex h-4 rounded-full overflow-hidden">
                <div 
                  className="bg-blue-500" 
                  style={{ width: `${result.principalPercent}%` }}
                  title={`Principal: ${result.principalPercent.toFixed(1)}%`}
                />
                <div 
                  className="bg-yellow-500" 
                  style={{ width: `${result.interestPercent}%` }}
                  title={`Interest: ${result.interestPercent.toFixed(1)}%`}
                />
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span> Principal
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span> Interest
                </span>
              </div>
            </div>
          </div>

          {/* Loan Summary */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-300 mb-4">Loan Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Loan Amount</span>
                <span className="text-white font-medium">{formatCurrency(result.loanAmount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Interest Rate</span>
                <span className="text-white font-medium">{result.interestRate}% per year</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Loan Term</span>
                <span className="text-white font-medium">
                  {result.termYears} years ({result.termMonths} payments)
                </span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Total of All Payments</span>
                <span className="text-white font-medium">{formatCurrency(result.totalPayment)}</span>
              </div>
            </div>
          </div>

          {/* First Year Breakdown */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-300 mb-3">First Year Breakdown</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex justify-between items-center p-2 bg-gray-900 rounded">
                <span className="text-sm text-gray-400">Principal Paid</span>
                <span className="text-blue-400 font-medium">{formatCurrency(result.firstYearPrincipal)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-gray-900 rounded">
                <span className="text-sm text-gray-400">Interest Paid</span>
                <span className="text-yellow-400 font-medium">{formatCurrency(result.firstYearInterest)}</span>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              In the first year, {((result.firstYearInterest / (result.firstYearPrincipal + result.firstYearInterest)) * 100).toFixed(1)}% of your payments go toward interest.
            </div>
          </div>

          {/* Amortization Schedule */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <button
              onClick={() => setShowSchedule(!showSchedule)}
              className="w-full flex items-center justify-between text-sm font-medium text-gray-300"
              data-testid="toggle-schedule"
            >
              <span>Amortization Schedule</span>
              {showSchedule ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            
            {showSchedule && (
              <div className="mt-4">
                {/* Schedule View Toggle */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setScheduleView('yearly')}
                    className={`px-3 py-1 rounded text-sm ${
                      scheduleView === 'yearly' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'
                    }`}
                  >
                    Yearly
                  </button>
                  <button
                    onClick={() => setScheduleView('monthly')}
                    className={`px-3 py-1 rounded text-sm ${
                      scheduleView === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'
                    }`}
                  >
                    Monthly
                  </button>
                </div>

                <div className="overflow-x-auto max-h-96" data-testid="schedule-table">
                  {scheduleView === 'yearly' ? (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-800">
                        <tr className="text-gray-400 border-b border-gray-700">
                          <th className="text-left py-2 pr-4">Year</th>
                          <th className="text-right py-2 px-2">Principal</th>
                          <th className="text-right py-2 px-2">Interest</th>
                          <th className="text-right py-2 px-2">Total Paid</th>
                          <th className="text-right py-2 pl-2">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.yearlySchedule.map((row) => (
                          <tr key={row.year} className="border-b border-gray-700/50">
                            <td className="py-2 pr-4 text-white">{row.year}</td>
                            <td className="py-2 px-2 text-right text-blue-400">{formatCurrency(row.principal)}</td>
                            <td className="py-2 px-2 text-right text-yellow-400">{formatCurrency(row.interest)}</td>
                            <td className="py-2 px-2 text-right text-green-400">{formatCurrency(row.total)}</td>
                            <td className="py-2 pl-2 text-right text-gray-300">{formatCurrency(row.endingBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-800">
                        <tr className="text-gray-400 border-b border-gray-700">
                          <th className="text-left py-2 pr-4">Month</th>
                          <th className="text-right py-2 px-2">Payment</th>
                          <th className="text-right py-2 px-2">Principal</th>
                          <th className="text-right py-2 px-2">Interest</th>
                          <th className="text-right py-2 pl-2">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.schedule.slice(0, 60).map((row) => (
                          <tr key={row.month} className="border-b border-gray-700/50">
                            <td className="py-2 pr-4 text-white">{row.month}</td>
                            <td className="py-2 px-2 text-right text-green-400">{formatCurrency(row.payment)}</td>
                            <td className="py-2 px-2 text-right text-blue-400">{formatCurrency(row.principal)}</td>
                            <td className="py-2 px-2 text-right text-yellow-400">{formatCurrency(row.interest)}</td>
                            <td className="py-2 pl-2 text-right text-gray-300">{formatCurrency(row.balance)}</td>
                          </tr>
                        ))}
                        {result.schedule.length > 60 && (
                          <tr>
                            <td colSpan="5" className="py-2 text-center text-gray-500">
                              ... {result.schedule.length - 60} more months
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Amortization Explanation */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <Info size={16} className="text-blue-400" />
          <h3 className="text-sm font-medium text-gray-300">What is Amortization?</h3>
        </div>
        <div className="space-y-3 text-sm text-gray-400">
          <p>
            <span className="text-white font-medium">Amortization</span> is the process of spreading a loan into 
            a series of fixed payments over time. Each payment covers both the interest expense and reduces the 
            principal balance.
          </p>
          <p>
            <span className="text-white font-medium">How it works:</span> In the early years of a loan, most of 
            your payment goes toward interest. As you pay down the principal, more of each payment goes toward 
            the principal itself. This is why making extra payments early can save significant interest.
          </p>
          <div className="p-3 bg-gray-900 rounded">
            <div className="text-xs text-gray-500 mb-2">Monthly Payment Formula:</div>
            <div className="font-mono text-yellow-400">
              M = P × [r(1+r)^n] / [(1+r)^n - 1]
            </div>
            <div className="text-xs text-gray-500 mt-2 space-y-1">
              <div><span className="text-gray-300">M</span> = Monthly payment</div>
              <div><span className="text-gray-300">P</span> = Principal (loan amount)</div>
              <div><span className="text-gray-300">r</span> = Monthly interest rate (annual rate ÷ 12)</div>
              <div><span className="text-gray-300">n</span> = Total number of payments</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Tips to Pay Off Faster</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• <span className="text-white">Make bi-weekly payments</span> - Results in one extra payment per year</div>
          <div>• <span className="text-white">Round up payments</span> - Even small extra amounts reduce interest</div>
          <div>• <span className="text-white">Apply windfalls to principal</span> - Bonuses, tax refunds go a long way</div>
          <div>• <span className="text-white">Refinance when rates drop</span> - Lower rate means more goes to principal</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-amortization-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default AmortizationCalculatorTool;
